import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProgressIndicator from './components/ProgressIndicator';
import QuestionCard from './components/QuestionCard';
import SectionHeader from './components/SectionHeader';
import ResultsCard from './components/ResultsCard';
import ContactForm from './components/ContactForm';
import generateDataMaturityPDF from '../../utils/pdfReportGenerator';
import { useAuth } from '../../contexts/AuthContext';
import { assessmentService } from '../../services/assessmentService';
import { trackAssessmentStart, trackAssessmentCompletion } from '../../utils/analyticsEvents';

const DataMaturityCalculator = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [assessmentStarted, setAssessmentStarted] = useState(false);

  const sections = [
    {
      id: 'dataInfrastructure',
      title: 'Data Infrastructure',
      icon: 'Database',
      description: 'Assess your data collection, storage, and integration capabilities',
      questions: [
        {
          id: 'di_1',
          title: 'How well integrated are your data sources?',
          description: 'Consider the connectivity between different systems, databases, and applications in your organization.',
          tooltip: 'Integration includes real-time data flows, API connections, and automated data synchronization across systems.'
        },
        {
          id: 'di_2',
          title: 'What is the quality and reliability of your data?',
          description: 'Evaluate data accuracy, completeness, consistency, and timeliness across your organization.',
          tooltip: 'High-quality data means minimal errors, complete records, consistent formats, and up-to-date information.'
        },
        {
          id: 'di_3',
          title: 'How scalable is your current data architecture?',
          description: 'Assess your infrastructure\'s ability to handle growing data volumes and user demands.',
          tooltip: 'Scalability includes cloud capabilities, storage expansion options, and performance under increased load.'
        },
        {
          id: 'di_4',
          title: 'How accessible is data to those who need it?',
          description: 'Consider ease of data access, self-service capabilities, and data discovery tools.',
          tooltip: 'Accessibility means users can find, understand, and use relevant data without extensive IT support.'
        }
      ]
    },
    {
      id: 'analyticsCapabilities',
      title: 'Analytics Capabilities',
      icon: 'BarChart3',
      description: 'Evaluate your analytical tools, processes, and insights generation',
      questions: [
        {
          id: 'ac_1',
          title: 'How advanced are your analytical tools and technologies?',
          description: 'Assess the sophistication of your BI tools, statistical software, and analytical platforms.',
          tooltip: 'Advanced tools include AI/ML capabilities, predictive analytics, real-time processing, and self-service analytics.'
        },
        {
          id: 'ac_2',
          title: 'How effectively do you generate actionable insights?',
          description: 'Evaluate your ability to transform data into meaningful business recommendations.',
          tooltip: 'Actionable insights lead to specific decisions, measurable outcomes, and clear business value.'
        },
        {
          id: 'ac_3',
          title: 'How well do you measure and track key performance indicators?',
          description: 'Consider your KPI framework, dashboard usage, and performance monitoring capabilities.',
          tooltip: 'Effective KPI tracking includes real-time monitoring, automated alerts, and clear performance benchmarks.'
        },
        {
          id: 'ac_4',
          title: 'How mature are your predictive and prescriptive analytics?',
          description: 'Assess your use of forecasting, machine learning, and optimization techniques.',
          tooltip: 'Mature predictive analytics includes automated model deployment, continuous learning, and business integration.'
        }
      ]
    },
    {
      id: 'governanceMaturity',
      title: 'Governance Maturity',
      icon: 'Shield',
      description: 'Review your data governance, security, and compliance frameworks',
      questions: [
        {
          id: 'gm_1',
          title: 'How comprehensive are your data governance policies?',
          description: 'Evaluate the completeness and enforcement of your data management policies and procedures.',
          tooltip: 'Comprehensive governance includes data ownership, quality standards, access controls, and lifecycle management.'
        },
        {
          id: 'gm_2',
          title: 'How well do you ensure data security and privacy?',
          description: 'Assess your data protection measures, access controls, and privacy compliance.',
          tooltip: 'Strong security includes encryption, access monitoring, privacy controls, and incident response procedures.'
        },
        {
          id: 'gm_3',
          title: 'How effectively do you manage data compliance and regulations?',
          description: 'Consider your adherence to GDPR, industry standards, and regulatory requirements.',
          tooltip: 'Effective compliance includes automated monitoring, audit trails, and proactive regulatory alignment.'
        },
        {
          id: 'gm_4',
          title: 'How well-defined are your data ownership and stewardship roles?',
          description: 'Evaluate the clarity of data responsibilities and accountability across your organization.',
          tooltip: 'Clear ownership includes designated data stewards, defined responsibilities, and accountability measures.'
        }
      ]
    },
    {
      id: 'teamSkills',
      title: 'Team Skills',
      icon: 'Users',
      description: 'Assess your team\'s data literacy, skills, and analytical capabilities',
      questions: [
        {
          id: 'ts_1',
          title: 'How data-literate is your organization overall?',
          description: 'Evaluate the general understanding and comfort level with data across all departments.',
          tooltip: 'Data literacy includes ability to read, understand, create, and communicate with data effectively.'
        },
        {
          id: 'ts_2',
          title: 'How skilled are your technical data professionals?',
          description: 'Assess the expertise of your data analysts, scientists, and engineers.',
          tooltip: 'Technical skills include programming, statistical analysis, machine learning, and data engineering capabilities.'
        },
        {
          id: 'ts_3',
          title: 'How effectively do you develop and retain data talent?',
          description: 'Consider your training programs, career development, and talent retention strategies.',
          tooltip: 'Talent development includes continuous learning, skill advancement programs, and competitive retention strategies.'
        },
        {
          id: 'ts_4',
          title: 'How well do business and technical teams collaborate on data initiatives?',
          description: 'Evaluate cross-functional collaboration and communication around data projects.',
          tooltip: 'Effective collaboration includes shared goals, clear communication, and integrated project workflows.'
        }
      ]
    },
    {
      id: 'strategicAlignment',
      title: 'Strategic Alignment',
      icon: 'Target',
      description: 'Evaluate how well your data initiatives align with business objectives',
      questions: [
        {
          id: 'sa_1',
          title: 'How well does your data strategy align with business goals?',
          description: 'Assess the connection between your data initiatives and overall business objectives.',
          tooltip: 'Strategic alignment means data projects directly support business priorities and measurable outcomes.'
        },
        {
          id: 'sa_2',
          title: 'How effectively do you measure ROI from data investments?',
          description: 'Evaluate your ability to quantify and demonstrate value from data and analytics projects.',
          tooltip: 'ROI measurement includes cost tracking, benefit quantification, and regular value assessment.'
        },
        {
          id: 'sa_3',
          title: 'How well do you prioritize and manage data initiatives?',
          description: 'Consider your project prioritization, resource allocation, and portfolio management.',
          tooltip: 'Effective prioritization includes business impact assessment, resource optimization, and strategic focus.'
        },
        {
          id: 'sa_4',
          title: 'How mature is your data-driven decision making culture?',
          description: 'Assess how consistently data influences decisions across all levels of your organization.',
          tooltip: 'Data-driven culture means decisions are regularly supported by evidence, analysis, and measurable insights.'
        }
      ]
    }
  ];

  const calculateResults = () => {
    const sectionScores = {};
    let totalQuestions = 0;
    let totalScore = 0;

    sections?.forEach(section => {
      const sectionAnswers = section?.questions?.map(q => answers?.[q?.id] || 0);
      const sectionAverage = sectionAnswers?.reduce((sum, score) => sum + score, 0) / section?.questions?.length;
      sectionScores[section.id] = sectionAverage;
      totalScore += sectionAverage;
      totalQuestions += section?.questions?.length;
    });

    const overallScore = totalScore / sections?.length;

    const generateRecommendations = () => {
      const recommendations = [];

      if (sectionScores?.dataInfrastructure < 3) {
        recommendations?.push(
          "Data Infrastructure Enhancement: Prioritize establishing a unified data integration platform to consolidate disparate data sources. Implement automated data quality monitoring with validation rules and cleansing workflows. Consider cloud-based data warehousing solutions (Snowflake, BigQuery, or Redshift) to improve scalability and accessibility. Expected outcome: 40-60% reduction in data preparation time and improved data reliability across the organization."
        );
      } else if (sectionScores?.dataInfrastructure < 4) {
        recommendations?.push(
          "Data Infrastructure Optimization: Enhance your existing infrastructure with real-time data streaming capabilities and advanced data cataloging. Implement data lineage tracking and metadata management to improve data discoverability. Consider adopting a data mesh architecture for better scalability. Expected outcome: 30% improvement in data accessibility and reduced time-to-insight."
        );
      }

      if (sectionScores?.analyticsCapabilities < 3) {
        recommendations?.push(
          "Analytics Capabilities Development: Invest in modern business intelligence platforms with self-service capabilities (Tableau, Power BI, or Looker) to democratize data access. Establish a center of excellence to develop standardized analytics methodologies and best practices. Implement predictive analytics pilots in high-impact business areas. Expected outcome: 3-5x increase in data-driven decision making across departments and 25% improvement in forecast accuracy."
        );
      } else if (sectionScores?.analyticsCapabilities < 4) {
        recommendations?.push(
          "Advanced Analytics Acceleration: Expand machine learning and AI capabilities with MLOps frameworks for model deployment and monitoring. Develop automated insight generation systems and anomaly detection. Create cross-functional analytics teams to tackle complex business challenges. Expected outcome: 50% faster insight delivery and identification of 10-15 new revenue or cost optimization opportunities annually."
        );
      }

      if (sectionScores?.governanceMaturity < 3) {
        recommendations?.push(
          "Data Governance Framework Implementation: Establish a formal data governance council with executive sponsorship and clear accountability structures. Define comprehensive data policies covering quality standards, access controls, retention, and privacy compliance (GDPR, CCPA). Implement data stewardship programs with designated owners for critical data domains. Deploy data classification and sensitive data discovery tools. Expected outcome: 70% reduction in compliance risks, improved data trust, and 40% faster regulatory reporting."
        );
      } else if (sectionScores?.governanceMaturity < 4) {
        recommendations?.push(
          "Governance Maturity Enhancement: Implement automated policy enforcement and continuous compliance monitoring. Expand governance to include AI/ML model governance and ethical AI frameworks. Develop data product management practices with clear SLAs. Expected outcome: Proactive risk mitigation and 50% reduction in governance-related incidents."
        );
      }

      if (sectionScores?.teamSkills < 3) {
        recommendations?.push(
          "Data Literacy and Skills Development: Launch organization-wide data literacy programs with role-based training paths (executive, analyst, citizen data scientist). Partner with universities or online platforms (Coursera, DataCamp) for structured upskilling. Establish mentorship programs pairing data experts with business users. Create internal data communities of practice for knowledge sharing. Consider hiring specialized roles: data engineers, analytics translators, and ML engineers. Expected outcome: 60% increase in self-sufficient data users, reduced dependency on IT, and improved data-driven culture."
        );
      } else if (sectionScores?.teamSkills < 4) {
        recommendations?.push(
          "Advanced Skills Cultivation: Develop specialized expertise in emerging technologies (generative AI, graph analytics, edge computing). Create innovation labs for experimentation with cutting-edge data technologies. Implement talent retention strategies including career progression frameworks for data professionals. Expected outcome: 40% improvement in advanced analytics project success rates and enhanced competitive advantage through innovation."
        );
      }

      if (sectionScores?.strategicAlignment < 3) {
        recommendations?.push(
          "Strategic Alignment Establishment: Develop a comprehensive data strategy document aligned with business objectives, including 3-year roadmap with prioritized initiatives. Implement value-based prioritization frameworks (business impact vs. effort matrices) for data projects. Establish clear KPIs and ROI measurement methodologies for all data initiatives. Create executive dashboards showing data initiative impact on business outcomes. Form a data strategy steering committee with C-suite representation. Expected outcome: 80% of data projects directly tied to strategic goals, 2x improvement in project ROI, and enhanced executive buy-in for data investments."
        );
      } else if (sectionScores?.strategicAlignment < 4) {
        recommendations?.push(
          "Strategic Value Maximization: Explore data monetization opportunities through new data products or services. Develop strategic partnerships for data sharing and ecosystem collaboration. Implement continuous value tracking and portfolio optimization for data initiatives. Expected outcome: Identification of 3-5 new revenue streams from data assets and 35% improvement in data investment returns."
        );
      }

      if (recommendations?.length === 0) {
        recommendations?.push(
          "Innovation and Leadership: Your organization demonstrates advanced data maturity. Focus on maintaining competitive advantage through cutting-edge innovations: Implement generative AI and large language models for business automation and insight generation. Develop real-time decision intelligence platforms combining streaming analytics with AI. Establish thought leadership through industry publications and speaking engagements. Expected outcome: Industry recognition as a data-driven leader and 20-30% competitive advantage in market responsiveness."
        );
        recommendations?.push(
          "Data Monetization and Ecosystem: Explore external data monetization through data-as-a-service offerings or strategic data partnerships. Create data marketplace platforms for internal and external data exchange. Develop industry-specific data products that can be commercialized. Expected outcome: New revenue streams contributing 5-10% of total revenue within 2-3 years and enhanced market positioning."
        );
        recommendations?.push(
          "Continuous Innovation Culture: Establish dedicated innovation teams focused on emerging data technologies (quantum computing for optimization, federated learning for privacy-preserving analytics). Create venture partnerships or corporate venture capital for data technology startups. Implement continuous learning systems that adapt to market changes. Expected outcome: Sustained innovation pipeline with 15-20 experimental projects annually and first-mover advantage in emerging data capabilities."
        );
      }

      return recommendations;
    };

    const getInterpretation = () => {
      if (overallScore >= 4.5) {
        return "Your organization demonstrates advanced data maturity with strong capabilities across all dimensions. Focus on innovation and competitive differentiation through data.";
      } else if (overallScore >= 3.5) {
        return "Your organization shows proficient data capabilities with room for optimization. Strategic investments in weaker areas will drive significant value.";
      } else if (overallScore >= 2.5) {
        return "Your organization is developing data capabilities but needs focused improvement in key areas. A structured transformation approach will accelerate progress.";
      } else {
        return "Your organization has foundational data challenges that require immediate attention. A comprehensive data strategy and roadmap are essential for success.";
      }
    };

    const getRoiImpact = () => {
      if (overallScore >= 4.5) return "15-25% efficiency gains possible";
      if (overallScore >= 3.5) return "20-35% efficiency gains possible";
      if (overallScore >= 2.5) return "25-45% efficiency gains possible";
      return "30-50% efficiency gains possible";
    };

    const maturityLevel = 
      overallScore >= 4.5 ? 'Optimized' :
      overallScore >= 3.5 ? 'Advanced' :
      overallScore >= 2.5 ? 'Intermediate' :
      overallScore >= 1.5 ? 'Developing' : 'Initial';

    const calculatedResults = {
      overallScore,
      maturityLevel,
      sectionScores,
      recommendations: generateRecommendations(),
      timestamp: new Date()?.toISOString()
    };

    setResults(calculatedResults);
    setShowResults(true);

    // Track assessment completion
    trackAssessmentCompletion('Data Maturity Assessment', overallScore, maturityLevel);

    return calculatedResults;
  };

  const handleAnswer = (questionId, value) => {
    // Track assessment start on first answer
    if (!assessmentStarted) {
      trackAssessmentStart('Data Maturity Assessment');
      setAssessmentStarted(true);
    }

    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const getCurrentSectionQuestions = () => {
    return sections?.[currentSection]?.questions || [];
  };

  const getCurrentSectionAnswers = () => {
    const questions = getCurrentSectionQuestions();
    return questions?.filter(q => answers?.[q?.id])?.length;
  };

  const getTotalAnswers = () => {
    return Object.keys(answers)?.length;
  };

  const getTotalQuestions = () => {
    return sections?.reduce((total, section) => total + section?.questions?.length, 0);
  };

  const canProceedToNext = () => {
    const questions = getCurrentSectionQuestions();
    return questions?.every(q => answers?.[q?.id]);
  };

  const handleNext = () => {
    if (currentSection < sections?.length - 1) {
      setCurrentSection(currentSection + 1);
    } else {
      const calculatedResults = calculateResults();
      setResults(calculatedResults);
      setShowContactForm(true);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleContactSubmit = async (contactData) => {
    setIsSubmitting(true);
    
    try {
      // Get or create session ID for conversion tracking
      let sessionId = sessionStorage.getItem('conversion_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
        sessionStorage.setItem('conversion_session_id', sessionId);
      }

      // Save assessment to Supabase if user is authenticated
      const { user } = useAuth();
      let assessmentId = null;
      
      if (user) {
        const savedAssessment = await assessmentService?.saveAssessment(
          'data_maturity',
          `Data Maturity Assessment - ${contactData?.company}`,
          { answers, contactData },
          results
        );
        assessmentId = savedAssessment?.id;
      }

      // Track assessment completion in Google Analytics
      const { trackAssessmentCompletion } = await import('../../utils/analyticsEvents');
      trackAssessmentCompletion(
        'data_maturity',
        results?.score,
        results?.maturityLevel
      );

      // Track assessment completion event in Supabase
      const { analyticsService } = await import('../../services/analyticsService');
      await analyticsService?.trackConversionEvent({
        sessionId,
        eventType: 'assessment_complete',
        eventCategory: 'Assessment',
        eventData: {
          assessmentType: 'data_maturity',
          score: results?.score,
          maturityLevel: results?.maturityLevel,
          company: contactData?.company
        }
      });

      // Create conversion funnel record
      await analyticsService?.upsertConversionFunnel({
        sessionId,
        assessmentId,
        assessmentType: 'data_maturity',
        assessmentScore: results?.score,
        maturityLevel: results?.maturityLevel,
        conversionStatus: 'assessment_only'
      });

      // Store assessment data in session for potential booking conversion tracking
      sessionStorage.setItem('assessment_data', JSON.stringify({
        assessmentType: 'data_maturity',
        score: results?.score,
        maturityLevel: results?.maturityLevel,
        completedAt: new Date()?.toISOString()
      }));
      sessionStorage.setItem('booking_source', 'assessment');
      
      // Generate PDF
      generateDataMaturityPDF(results, answers, contactData);
      
      // Show success message
      alert('Your report has been generated and downloaded!');
    } catch (error) {
      console.error('Error saving assessment:', error);
      alert('Report generated, but there was an issue saving to your account.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadReport = () => {
    if (results) {
      generateDataMaturityPDF(results, answers, sections);
    }
  };

  const handleBookConsultation = () => {
    window.location.href = '/contact-multi-channel-engagement-hub';
  };

  const getCompletedSections = () => {
    let completed = 0;
    sections?.forEach(section => {
      const sectionComplete = section?.questions?.every(q => answers?.[q?.id]);
      if (sectionComplete) completed++;
    });
    return completed;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentSection]);

  if (showResults && results) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <Link 
                to="/"
                className="inline-flex items-center text-secondary hover:text-secondary/80 transition-colors duration-200 mb-4"
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Home
              </Link>
              <h1 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
                Your Data Maturity Assessment Results
              </h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Based on your responses, here's your comprehensive data maturity analysis with personalized recommendations.
              </p>
            </div>

            <ResultsCard
              results={results}
              onDownloadReport={handleDownloadReport}
              onBookConsultation={handleBookConsultation}
            />

            <div className="mt-12 bg-white rounded-lg shadow-subtle p-8 text-center">
              <h3 className="text-xl font-semibold text-primary mb-4">Ready to Transform Your Data Strategy?</h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Our data strategy experts can help you implement these recommendations and accelerate your data maturity journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={handleBookConsultation}
                  className="bg-secondary hover:bg-secondary/90"
                >
                  Schedule Free Consultation
                </Button>
                <Button
                  variant="outline"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => window.location.href = '/services-overview-interactive-capability-showcase'}
                >
                  Explore Our Services
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (showContactForm && results) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
                Almost There!
              </h1>
              <p className="text-muted-foreground text-lg">
                Complete your profile to receive your detailed data maturity report and personalized recommendations.
              </p>
            </div>

            <ContactForm
              onSubmit={handleContactSubmit}
              isLoading={isSubmitting}
            />

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowContactForm(false)}
                className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm"
              >
                ← Back to Assessment
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Link 
              to="/"
              className="inline-flex items-center text-secondary hover:text-secondary/80 transition-colors duration-200 mb-4"
            >
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
              Data Maturity Assessment
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Discover your organization's data maturity level and get personalized recommendations 
              to accelerate your data transformation journey.
            </p>
          </div>

          {/* Progress Indicator */}
          <ProgressIndicator
            currentStep={currentSection + 1}
            totalSteps={sections?.length}
            completedSteps={getCompletedSections()}
          />

          {/* Section Header */}
          <SectionHeader
            section={sections?.[currentSection]}
            isActive={true}
            isCompleted={false}
          />

          {/* Questions */}
          <div className="space-y-6 mb-8">
            {getCurrentSectionQuestions()?.map((question) => (
              <QuestionCard
                key={question?.id}
                question={question}
                currentAnswer={answers?.[question?.id]}
                onAnswer={handleAnswer}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg shadow-subtle p-4 sm:p-6 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 sm:gap-0">
              <div className="text-xs sm:text-sm text-muted-foreground">
                Section {currentSection + 1} of {sections?.length}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">
                {getCurrentSectionAnswers()} of {getCurrentSectionQuestions()?.length} questions answered
              </div>
            </div>

            <div className="flex items-center space-x-3 sm:space-x-4">
              {currentSection > 0 && (
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={handlePrevious}
                  className="flex-1 sm:flex-none"
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </Button>
              )}
              
              <Button
                variant="default"
                iconName={currentSection === sections?.length - 1 ? "CheckCircle" : "ArrowRight"}
                iconPosition="right"
                onClick={handleNext}
                disabled={!canProceedToNext()}
                className="bg-secondary hover:bg-secondary/90 flex-1 sm:flex-none"
              >
                {currentSection === sections?.length - 1 ? (
                  <>
                    <span className="hidden sm:inline">Complete Assessment</span>
                    <span className="sm:hidden">Complete</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Next Section</span>
                    <span className="sm:hidden">Next</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Assessment Info */}
          <div className="mt-6 sm:mt-8 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Info" size={20} className="text-secondary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-primary mb-2 text-sm sm:text-base">About This Assessment</h3>
                <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed mb-3">
                  This comprehensive assessment evaluates your organization across five critical dimensions of data maturity. 
                  Your responses help us understand your current state and provide targeted recommendations for improvement.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} />
                    <span>10-15 minutes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={14} />
                    <span>Confidential & Secure</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Award" size={14} />
                    <span>Personalized Report</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DataMaturityCalculator;